import { Injectable } from '@angular/core';

let uid = 0;
const defaultMaxBubbles = 20;

export interface BubbleOptions {
  xLowerBound: number;
  xUpperBound: number;
  yLowerBound: number;
  yUpperBound: number;
  x: number;
  y: number;
  minSpeed: number;
  maxSpeed: number;
  minOpacity: number;
  maxOpacity: number;
  minSize: number;
  maxSize: number;
}

export class BubbleOptionFactory {
  create(): BubbleOptions {
    return {
      minSize: 3,
      maxSize: 8,
      minOpacity: 0.05,
      maxOpacity: 0.15,
      minSpeed: 0.01,
      maxSpeed: 0.05,
      xLowerBound: 0,
      xUpperBound: window.innerWidth,
      yLowerBound: 0,
      yUpperBound: window.innerHeight,
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
    };
  }
}

function randomNumber(min, max) {
  return Math.random() * max + min;
}
function randomBool() {
  return Math.random() < 0.5;
}

export class PgBubble {
  id: number;

  xPos = 0;
  yPos = 0;

  xTarget = 0;
  yTarget = 0;

  opacity = 0;
  speed = 1;
  size = 1;

  _rotationFactor = 50;
  _rotationLg = 0;
  _rotDirection = 1;
  rotation = 0;

  canDestroy = false;

  deleteStartFrame = null;
  opacityStep;
  state: 'running' | 'deleting' = 'running';

  constructor(public options: BubbleOptions) {
    this.id = uid++;

    this.xPos = options.x;
    this.yPos = options.y;
    this.speed = randomNumber(options.minSpeed, options.maxSpeed);
    this.opacity = randomNumber(options.minOpacity, options.maxOpacity);
    this.size = randomNumber(options.minSize, options.maxSize);
    this._rotDirection = randomBool() ? 1 : -1;
    this.rotation = randomNumber(0, 359);
    this._rotationLg = this.rotation * this._rotationFactor;
    this.setRandomVelocity();
  }

  update(frame) {
    if (this.state === 'running') {
      this.runningFrame(frame);
    } else if (this.state === 'deleting') {
      this.deletingFrame(frame);
    }
  }

  runningFrame(frame) {
    if (
      this.xTarget === this.options.xUpperBound &&
      this.xPos >= this.options.xUpperBound
    ) {
      this.xTarget = this.options.xLowerBound;
    } else if (
      this.yTarget === this.options.yUpperBound &&
      this.yPos >= this.options.yUpperBound
    ) {
      this.yTarget = this.options.yLowerBound;
    } else if (this.xTarget === this.options.xLowerBound && this.xPos <= 0) {
      this.xTarget = this.options.xUpperBound;
    } else if (this.yTarget === this.options.yLowerBound && this.yPos <= 0) {
      this.yTarget = this.options.yUpperBound;
    } else {
      this.xPos += this.xTarget < this.xPos ? -this.speed : this.speed;
      this.yPos += this.yTarget < this.yPos ? -this.speed : this.speed;
    }
    this._rotationLg += this._rotDirection;
    this.rotation = (this._rotationLg / this._rotationFactor) % 360;
  }

  deletingFrame(frame) {
    const deletionFramesCount = 100;
    if (!this.deleteStartFrame && this.deleteStartFrame !== 0) {
      this.deleteStartFrame = frame;
      this.opacityStep = this.opacity / deletionFramesCount;
    }

    this.opacity = Math.max(0, this.opacity - this.opacityStep);
    if (frame - this.deleteStartFrame >= deletionFramesCount) {
      this.canDestroy = true;
    }
  }

  delete() {
    this.state = 'deleting';
  }

  setRandomVelocity() {
    this.xTarget = !!randomBool()
      ? this.options.xUpperBound
      : this.options.xLowerBound;
    this.yTarget = !!randomBool()
      ? this.options.yUpperBound
      : this.options.yLowerBound;
  }
}

@Injectable()
export class PgBubbleService {
  status: 'running' | 'paused' | 'stopped';

  bubbles: PgBubble[] = [];

  maxBubbles = defaultMaxBubbles;

  frames = 0;

  animationFrameRef;

  constructor() {}

  update() {
    let cleanupMarked = false;
    for (let i = 0; i < this.bubbles.length; i++) {
      this.bubbles[i].update(this.frames);
      if (this.bubbles[i].canDestroy) {
        cleanupMarked = true;
      }
    }
    if (cleanupMarked) {
      this.bubbles = this.bubbles.filter(b => !b.canDestroy);
    }
    this.frames++;
  }

  start() {
    const engine = () => {
      this.update();
      this.animationFrameRef = requestAnimationFrame(() => engine());
    };
    engine();
  }

  stop() {
    cancelAnimationFrame(this.animationFrameRef);
  }

  addBubble(bubble?: { x: number; y: number }) {
    let calcBubble: PgBubble;

    const fact = new BubbleOptionFactory();
    const opts = fact.create();
    if (bubble) {
      opts.x = bubble.x;
      opts.y = bubble.y;
    }
    calcBubble = new PgBubble(opts);

    this.bubbles.push(calcBubble);
    if (this.bubbles.length > this.maxBubbles) {
      const deletable = this.bubbles.find(b => b.state !== 'deleting');
      if (deletable) {
        deletable.delete();
      }
    }
  }
}
